// -----------------------------------------------------------
// FloraIQ - NewSurveyModal — Modal de Novo Levantamento
// -----------------------------------------------------------

import { useState } from "react";
import { surveyTypes, surveyEquipment } from "@/mocks/pointcloud";

interface NewSurveyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function NewSurveyModal({ isOpen, onClose }: NewSurveyModalProps) {
  const [step, setStep] = useState(1);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-white rounded-card w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto fade-in">
        <div className="sticky top-0 bg-white border-b border-background-200/70 px-5 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-foreground-900 font-heading">Novo Levantamento</h3>
            <p className="text-xs text-foreground-500 mt-0.5">Cadastre um novo levantamento com drone, LiDAR, laser scanner ou GNSS</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer" aria-label="Fechar">
            <i className="ri-close-line text-foreground-600"></i>
          </button>
        </div>

        {/* Steps */}
        <div className="px-5 pt-4">
          <div className="flex items-center gap-2 mb-5">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${s <= step ? "bg-primary-500 text-white" : "bg-background-100 text-foreground-400"}`}>
                  {s < step ? <i className="ri-check-line text-white"></i> : s}
                </div>
                <span className={`text-xs font-medium ${s <= step ? "text-foreground-700" : "text-foreground-400"}`}>
                  {s === 1 ? "Dados Básicos" : s === 2 ? "Equipamento" : "Arquivos"}
                </span>
                {s < 3 && <div className={`w-8 h-0.5 ${s < step ? "bg-primary-500" : "bg-background-200"}`}></div>}
              </div>
            ))}
          </div>
        </div>

        <div className="px-5 pb-5">
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">Nome do levantamento <span className="text-red-500">*</span></label>
                <input type="text" placeholder="Ex: Levantamento LiDAR — Setor Central" className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-white" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1">Projeto <span className="text-red-500">*</span></label>
                  <select className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-white cursor-pointer">
                    <option value="">Selecionar...</option>
                    <option>CTA Centro</option>
                    <option>CTA Setor Bueno</option>
                    <option>CTA Setor Oeste</option>
                    <option>CTA Setor Marista</option>
                    <option>CTA Jardim Goiás</option>
                    <option>CTA Aparecida de Goiânia</option>
                    <option>Parques Urbanos</option>
                    <option>Unidades de Conservação</option>
                    <option>Áreas Verdes</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1">Município/UF <span className="text-red-500">*</span></label>
                  <input type="text" placeholder="Goiânia / GO" className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-white" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1">Tipo de levantamento <span className="text-red-500">*</span></label>
                  <select className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-white cursor-pointer">
                    <option value="">Selecionar...</option>
                    {surveyTypes.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1">Data da coleta <span className="text-red-500">*</span></label>
                  <input type="date" className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-white cursor-pointer" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">Responsável <span className="text-red-500">*</span></label>
                <input type="text" placeholder="Nome do responsável técnico" className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-white" />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1">Equipamento <span className="text-red-500">*</span></label>
                  <select className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-white cursor-pointer">
                    <option value="">Selecionar...</option>
                    {surveyEquipment.map((e) => <option key={e}>{e}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1">Sensor</label>
                  <input type="text" placeholder="Modelo do sensor" className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-white" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1">Precisão estimada (cm)</label>
                  <input type="number" step="0.1" placeholder="Ex: 2,5" className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-white" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1">SRID</label>
                  <input type="text" placeholder="EPSG:31982" className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-white" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">Área coberta (ha)</label>
                <input type="number" step="0.1" placeholder="Ex: 380" className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-white" />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">Arquivo principal</label>
                <div className="border-2 border-dashed border-background-200/70 rounded-lg p-6 text-center cursor-pointer hover:border-primary-400 transition-colors">
                  <i className="ri-upload-cloud-2-line text-3xl text-foreground-400 mb-2 block"></i>
                  <p className="text-sm text-foreground-600">Clique para selecionar ou arraste o arquivo</p>
                  <p className="text-xs text-foreground-400 mt-1">LAS, LAZ, E57, PTX, GeoTIFF, OBJ</p>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">Arquivos auxiliares</label>
                <div className="border-2 border-dashed border-background-200/70 rounded-lg p-4 text-center cursor-pointer hover:border-primary-400 transition-colors">
                  <i className="ri-file-add-line text-2xl text-foreground-400 mb-1 block"></i>
                  <p className="text-xs text-foreground-500">Anexar arquivos auxiliares (opcional)</p>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">Observações</label>
                <textarea rows={3} placeholder="Observações técnicas sobre o levantamento..." className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-white resize-none"></textarea>
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-background-200/70 px-5 py-3 flex items-center justify-between">
          <button onClick={onClose} className="btn-outline text-xs px-4 py-2 cursor-pointer whitespace-nowrap">Cancelar</button>
          <div className="flex gap-2">
            {step > 1 && (
              <button onClick={() => setStep(step - 1)} className="btn-outline text-xs px-4 py-2 cursor-pointer whitespace-nowrap">
                <i className="ri-arrow-left-line mr-1"></i> Anterior
              </button>
            )}
            {step < 3 ? (
              <button onClick={() => setStep(step + 1)} className="btn-primary text-xs px-4 py-2 cursor-pointer whitespace-nowrap">
                Próximo <i className="ri-arrow-right-line ml-1"></i>
              </button>
            ) : (
              <button onClick={onClose} className="btn-primary text-xs px-4 py-2 cursor-pointer whitespace-nowrap">
                <i className="ri-check-line mr-1"></i> Cadastrar Levantamento
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}